import { Link, useSearchParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { GalleryAlbum } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { mediaUrl } from "@/lib/media";

const PAGE_SIZE = 12;

function formatDate(d?: string): string {
  if (!d) return "";
  return String(d).slice(0, 10).replace(/-/g, ".");
}

export default function GalleryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const { data, total, loading, error, reload } = useApi<GalleryAlbum[]>(
    `gallery.php?page=${page}&size=${PAGE_SIZE}`
  );
  const albums = data ?? [];
  const totalPages = Math.max(1, Math.ceil((total || albums.length) / PAGE_SIZE));

  const goPage = (p: number) => {
    if (p === 1) setSearchParams({});
    else setSearchParams({ page: String(p) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <PageBanner
        title="활동갤러리"
        description="클럽의 생생한 봉사와 활동 현장을 사진으로 만나보세요."
        breadcrumb={[{ label: "활동갤러리" }]}
      />

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="활동갤러리를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : albums.length === 0 ? (
          <EmptyState icon="ri-image-line" title="등록된 활동 사진이 없습니다" />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {albums.map((a) => {
                const thumb = a.thumb || a.photo || "";
                const title = a.title || a.subject || "활동 사진";
                return (
                  <Link
                    key={a.no}
                    to={`/gallery/${a.no}`}
                    className="group overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
                  >
                    <div className="relative h-48 w-full overflow-hidden bg-background-100">
                      {thumb ? (
                        <img
                          src={mediaUrl(thumb)}
                          alt={title}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-foreground-300">
                          <i className="ri-image-line text-3xl" aria-hidden="true"></i>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="line-clamp-1 font-heading text-base font-bold text-foreground-950">
                        {title}
                      </h3>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        {formatDate(a.regdate || a.reg_date) && (
                          <span>{formatDate(a.regdate || a.reg_date)}</span>
                        )}
                        {typeof a.cnt === "number" && (
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-camera-line" aria-hidden="true"></i>
                            {a.cnt}장
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {totalPages > 1 && (
              <nav className="mt-8 flex items-center justify-center gap-1.5" aria-label="페이지 이동">
                <button
                  type="button"
                  onClick={() => goPage(page - 1)}
                  disabled={page <= 1}
                  aria-label="이전 페이지"
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goPage(p)}
                      aria-current={p === page ? "page" : undefined}
                      className={`min-w-9 h-9 px-2 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                        p === page
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-200 text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => goPage(page + 1)}
                  disabled={page >= totalPages}
                  aria-label="다음 페이지"
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </button>
              </nav>
            )}
          </>
        )}
      </section>
    </>
  );
}